import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [gallery] - The scrollable zoom gallery.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.refs.gallery?.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
    this.refs.gallery?.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      this.#pauseGalleryMedia();
      this.#setGalleryVisible(false);
      dialog.showModal();
      this.#activeIndex = index;
      this.#updateNavigationState(index);
      this.#ensureVideoPlaying(targetImage);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Moves to the next media item in the zoom dialog.
   * @param {PointerEvent} event
   */
  next(event) {
    event.preventDefault();
    event.stopPropagation();

    const nextIndex = Math.min(this.#activeIndex + 1, this.refs.media.length - 1);
    this.selectThumbnail(nextIndex);
  }

  /**
   * Moves to the previous media item in the zoom dialog.
   * @param {PointerEvent} event
   */
  previous(event) {
    event.preventDefault();
    event.stopPropagation();

    const previousIndex = Math.max(this.#activeIndex - 1, 0);
    this.selectThumbnail(previousIndex);
  }

  /**
   * Resumes a native autoplay video that was mounted while the dialog was still closed
   * (browsers commonly refuse to start/keep playing a <video autoplay> that mounts hidden,
   * and nothing else re-triggers it once the dialog becomes visible).
   * @param {HTMLElement} [mediaContainer] - The media container element.
   */
  #ensureVideoPlaying(mediaContainer) {
    const video = mediaContainer?.querySelector('video');
    if (video instanceof HTMLVideoElement && video.paused && video.hasAttribute('autoplay')) {
      video.play().catch(() => {});
    }
  }

  /**
   * Pauses any playing video/iframe inside every <deferred-media> found within the given root.
   * @param {Element | null | undefined} root
   */
  #pauseMediaIn(root) {
    root?.querySelectorAll('deferred-media').forEach((player) => {
      if (typeof (/** @type {any} */ (player).pauseMedia) === 'function') {
        /** @type {any} */ (player).pauseMedia();
      }
    });
  }

  /**
   * Pauses the main product gallery's media (outside this zoom dialog) so it can't keep
   * playing/sounding once the zoomed copy takes over.
   */
  #pauseGalleryMedia() {
    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    mediaGallery.querySelectorAll('deferred-media').forEach((player) => {
      if (this.contains(player)) return; // this dialog's own copies are handled separately
      if (typeof (/** @type {any} */ (player).pauseMedia) === 'function') {
        /** @type {any} */ (player).pauseMedia();
      }
    });
  }

  /**
   * Hides/shows the main product gallery's own markup (everything in <media-gallery> except this
   * zoom dialog) while the dialog is open. The dialog's ::backdrop isn't guaranteed fully opaque
   * (this theme's zoom dialog doesn't use the "dialog-modal" class other dialogs use for that), so a
   * paused-but-still-visible video behind it could otherwise show/bleed through.
   * @param {boolean} visible
   */
  #setGalleryVisible(visible) {
    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    for (const child of mediaGallery.children) {
      if (/** @type {unknown} */ (child) === this) continue;
      if (child instanceof HTMLElement) child.style.visibility = visible ? '' : 'hidden';
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    const previousImage = media[this.#activeIndex];

    this.#activeIndex = activeIndex;
    this.#updateNavigationState(activeIndex);
    this.#loadHighResolutionImage(mostVisibleElement);

    if (previousImage && previousImage !== mostVisibleElement) {
      this.#pauseMediaIn(previousImage);
    }

    this.#ensureVideoPlaying(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the dialog when clicking the empty backdrop area.
   * @param {PointerEvent} event
   */
  #handleDialogClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, .dialog-thumbnails-list, img, video, iframe, model-viewer')) return;
    if (event.target !== this.refs.dialog && !event.target.closest('.dialog-zoomed-gallery')) return;

    this.close();
  };

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    // TODO: This is breaking component boundaries and should be refactored
    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#pauseMediaIn(this);
    dialog.close();
    this.#setGalleryVisible(true);
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    const previousImage = media[this.#activeIndex];

    this.#activeIndex = index;
    this.#updateNavigationState(index);

    if (previousImage && previousImage !== targetImage) {
      this.#pauseMediaIn(previousImage);
    }

    this.#ensureVideoPlaying(targetImage);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Updates the arrow disabled states.
   * @param {number} index
   */
  #updateNavigationState(index) {
    const previous = this.querySelector('[data-zoom-previous]');
    const next = this.querySelector('[data-zoom-next]');

    if (previous instanceof HTMLButtonElement) previous.disabled = index <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = index >= this.refs.media.length - 1;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
